/**
 * Post-process yonetimi tek yerde.
 *
 * Classic WebGL: EffectComposer + Pass (addPass / removePass).
 * WebGPU: generic TslEffect zinciri (addEffect / removeEffect).
 *
 * Controller AO/Outline/TAA bilmez — sadece kayitli effect'leri compose eder.
 * Effect'leri Lit element / Entity'ler yaratip register eder.
 */
import {
  LinearFilter,
  RGBAFormat,
  Scene,
  Vector2,
  WebGLRenderer,
  WebGLRenderTarget,
  type Camera,
  type Texture,
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { Pass } from "three/examples/jsm/postprocessing/Pass";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { RenderPipeline, type WebGPURenderer } from "three/webgpu";
import {
  diffuseColor,
  mix,
  mrt,
  normalView,
  normalWorld,
  output,
  pass,
  positionView,
  positionWorld,
  vec4,
  velocity,
} from "three/tsl";
import type { Engine } from "../Engine";
import { logger } from "../../util/Logger";

/** Scene MRT buffers exposed to each TSL effect while composing. */
export type TslEffectContext = {
  scene: Scene;
  camera: Camera;
  /**
   * Original scene color as PassNode (official outline adds onto this;
   * do not unwrap with getTextureNode for outline merge).
   */
  beauty: unknown;
  depth: unknown;
  normal: unknown;
  /** Albedo / diffuseColor MRT (SSGI blend). */
  diffuse: unknown;
  velocity: unknown;
  /**
   * Color-MRT copy of fragment depth (RGBA). Hardware depth textures often
   * cannot be sampled in the post graph; this can.
   */
  packedDepth: unknown;
  /** Sampleable RGBA copy of fragment depth (PassNode.getTexture). */
  packedDepthTexture?: Texture;
  /** Hardware scene-pass depth texture (PassNode.getTexture("depth")). */
  depthTexture?: Texture;
  /** WebGPU often uses reversed-Z; AO must not darken sky/clear depth. */
  reversedDepth: boolean;
};

/**
 * How an effect participates in the TSL graph:
 * - main: folds over beauty (AO / SSGI)
 * - overlay: parallel branch from scene; merged after main (outline)
 * - post: runs after merge (TRAA)
 */
export type TslEffectBranch = "main" | "overlay" | "post";

/**
 * Generic WebGPU post effect. Owned by Lit postprocess entities;
 * PostProcessingController only folds them in order.
 */
export interface TslEffect {
  /** Lower runs earlier within the same branch (default 0). */
  readonly order?: number;
  /** Pipeline branch — default `main`. */
  readonly branch?: TslEffectBranch;
  /** Needs normal MRT attachment. */
  readonly needsNormal?: boolean;
  /** Needs diffuseColor MRT (SSGI albedo blend). */
  readonly needsDiffuse?: boolean;
  /** Encode world-space normals in the normal MRT. */
  readonly needsWorldNormal?: boolean;
  /** Needs velocity MRT attachment (TRAA). */
  readonly needsVelocity?: boolean;
  /** Pack fragment depth into a sampleable RGBA MRT. */
  readonly needsPackedDepth?: boolean;
  /**
   * When false, effect already applied tone mapping / sRGB
   * (e.g. FXAA via renderOutput) — disable pipeline outputColorTransform.
   */
  readonly needsOutputColorTransform?: boolean;
  /**
   * When false, effect stays registered (selection wiring, etc.) but does not
   * keep the TSL RenderPipeline alive. Default: active.
   */
  isActive?(): boolean;
  /**
   * Main/post fold: compose onto the previous node in that branch.
   * Overlay effects may no-op here; they use `composeOverlay` instead.
   */
  compose(input: unknown, ctx: TslEffectContext): unknown;
  /**
   * Overlay branch: return additive color (or null to skip).
   * Does not take main-chain input — uses ctx.scene / ctx.beauty.
   */
  composeOverlay?(ctx: TslEffectContext): unknown | null;
}

export class PostProcessingController {
  private _composer?: EffectComposer;
  private readonly _outputPass = new OutputPass();
  private _warnedUnsupported = false;

  private readonly _effects: TslEffect[] = [];
  private _pipeline?: RenderPipeline;
  private _scenePass: ReturnType<typeof pass> | undefined;
  private _effectsDirty = true;

  constructor(private readonly _engine: Engine) {}

  get composer(): EffectComposer | undefined {
    return this._composer;
  }

  /**
   * True when at least one effect is contributing (SSGI/GTAO/TAA/outline…).
   * When false, MainRenderSystem must draw plain beauty (`renderer.render`).
   */
  get usesTslPipeline(): boolean {
    return this._activeEffects().length > 0;
  }

  /** Effects that currently contribute to the graph (see `isActive`). */
  private _activeEffects(): TslEffect[] {
    return this._effects.filter((e) => e.isActive?.() !== false);
  }

  private _supportsClassic(): boolean {
    return this._engine.renderer.supportsClassicPostProcessing;
  }

  private _warnUnsupportedOnce(): void {
    if (this._warnedUnsupported) return;
    this._warnedUnsupported = true;
    logger.warn(
      "Classic EffectComposer is disabled on WebGPURenderer; register TslEffect via addEffect.",
    );
  }

  // ---------- Generic TSL effect registry ----------

  addEffect(effect: TslEffect): void {
    if (this._effects.includes(effect)) return;
    this._effects.push(effect);
    this._effectsDirty = true;
    this._engine.dispatchCustomEvent("request-update");
  }

  removeEffect(effect: TslEffect): void {
    const i = this._effects.indexOf(effect);
    if (i < 0) return;
    this._effects.splice(i, 1);
    this._effectsDirty = true;
    this._engine.dispatchCustomEvent("request-update");
  }

  hasEffect(effect: TslEffect): boolean {
    return this._effects.includes(effect);
  }

  /** Find a registered effect by predicate (e.g. outline selection wiring). */
  findEffect<T extends TslEffect>(
    predicate: (effect: TslEffect) => effect is T,
  ): T | undefined {
    return this._effects.find(predicate);
  }

  /** Force TSL graph rebuild (e.g. outline entered/left the chain). */
  invalidateTslEffects(): void {
    this._effectsDirty = true;
    this._engine.dispatchCustomEvent("request-update");
  }

  // ---------- Classic EffectComposer ----------

  setRenderPassScene(target: Scene): void {
    if (!this._composer) return;
    const renderPass = this._composer.passes.find(
      (p) => p instanceof RenderPass,
    ) as RenderPass | undefined;
    if (renderPass) renderPass.scene = target;
  }

  updateCameraScene(): void {
    const cam = this._engine.activeCamera?.instance;
    // New camera object → rebuild so AO/TRAA passes pick it up.
    // Same camera instance moving does not need a graph rebuild.
    if (this._scenePass && cam && this._scenePass.camera !== cam) {
      this._effectsDirty = true;
    }

    if (!this._composer) return;
    const renderPass = this._composer.passes.find(
      (p) => p instanceof RenderPass,
    ) as RenderPass | undefined;
    if (!renderPass || !cam) return;
    renderPass.camera = cam;
    const scene = this._engine.activeScene;
    if (!this._engine.useRenderSceneSwap && scene) {
      renderPass.scene = scene;
    }
  }

  mutate(mutate: (composer: EffectComposer) => void): void {
    if (!this._supportsClassic()) {
      this._warnUnsupportedOnce();
      return;
    }
    const composer = this.getOrCreate();
    if (!composer) return;
    this._withoutTailPasses(composer, () => mutate(composer));
  }

  mutateIfExists(mutate: (composer: EffectComposer) => void): void {
    if (!this._composer) return;
    this._withoutTailPasses(this._composer, () => mutate(this._composer!));
  }

  addPass(passInst?: Pass): void {
    if (!passInst) return;
    if (!this._supportsClassic()) {
      this._warnUnsupportedOnce();
      return;
    }
    const composer = this.getOrCreate();
    if (!composer) return;
    composer.removePass(this._outputPass);
    composer.addPass(passInst);
    composer.addPass(this._outputPass);
  }

  removePass(passInst?: Pass): void {
    if (!passInst) return;
    this._composer?.removePass(this._outputPass);
    this._composer?.removePass(passInst);
    this._composer?.addPass(this._outputPass);
  }

  hasPass(passInst?: Pass): Pass | undefined {
    return this._composer?.passes.find((p) => p == passInst);
  }

  getOrCreate(): EffectComposer | undefined {
    if (!this._supportsClassic()) {
      this._warnUnsupportedOnce();
      return undefined;
    }
    if (!this._composer) {
      const parameters = {
        minFilter: LinearFilter,
        magFilter: LinearFilter,
        format: RGBAFormat,
        stencilBuffer: false,
        samples: 0,
      };

      const instance = this._engine.renderer.instance;
      const size = instance.getSize(new Vector2());
      const pixelRatio = instance.getPixelRatio();
      const w = size.width || this._engine.width;
      const h = size.height || this._engine.height;

      const renderTarget = new WebGLRenderTarget(
        w * pixelRatio,
        h * pixelRatio,
        parameters,
      );
      renderTarget.texture.name = "SEffectComposer.rt1";
      this._composer = new EffectComposer(
        instance as WebGLRenderer,
        renderTarget,
      );
      this._composer.setPixelRatio(pixelRatio);

      this._composer.addPass(
        new RenderPass(
          this._engine.activeScene!,
          this._engine.activeCamera!.instance,
        ),
      );
    }
    return this._composer;
  }

  private _withoutTailPasses(
    composer: EffectComposer,
    fn: () => void,
  ): void {
    composer.removePass(this._outputPass);
    try {
      fn();
    } finally {
      composer.addPass(this._outputPass);
    }
  }

  // ---------- TSL pipeline render ----------

  /**
   * Render the TSL effect graph. Returns false when the graph could not be
   * built — caller must fall back to plain beauty (`renderer.render`).
   */
  renderTsl(): boolean {
    if (!this._ensureTslBuilt() || !this._pipeline) return false;
    // Keep pass in sync with the same scene/camera the plain render path uses
    // (render-scene swap + ortho↔perspective camera swap on 2D/3D).
    if (this._scenePass) {
      const scene = this._engine.getSceneForWebGLRender();
      const camera = this._engine.activeCamera?.instance;
      if (scene) this._scenePass.scene = scene;
      if (camera) this._scenePass.camera = camera;
    }
    this._pipeline.render();
    return true;
  }

  private _ensureTslBuilt(): boolean {
    if (this._activeEffects().length === 0) return false;

    const scene = this._engine.getSceneForWebGLRender();
    const camera = this._engine.activeCamera?.instance;
    const renderer = this._engine.renderer.instance as WebGPURenderer;
    if (!scene || !camera) return false;

    if (!this._pipeline) {
      this._pipeline = new RenderPipeline(renderer);
      this._effectsDirty = true;
    }
    // Camera object changes on 2D↔3D (ortho↔perspective) — must rebuild.
    if (
      this._scenePass &&
      (this._scenePass.camera !== camera || this._scenePass.scene !== scene)
    ) {
      this._effectsDirty = true;
    }
    if (!this._effectsDirty && this._scenePass) return true;

    try {
      this._rebuildTsl(scene, camera);
      this._effectsDirty = false;
      return true;
    } catch (error) {
      logger.error("TSL post-processing rebuild failed", error);
      return false;
    }
  }

  private _rebuildTsl(scene: Scene, camera: Camera): void {
    const effects = this._activeEffects();
    const needsNormal = effects.some((e) => e.needsNormal);
    const needsDiffuse = effects.some((e) => e.needsDiffuse);
    const needsVelocity = effects.some((e) => e.needsVelocity);
    const needsPackedDepth = effects.some((e) => e.needsPackedDepth);
    const needsWorldNormal = effects.some((e) => e.needsWorldNormal);
    const needsMrt =
      needsNormal || needsDiffuse || needsVelocity || needsPackedDepth;

    const renderer = this._engine.renderer.instance as WebGPURenderer & {
      reversedDepthBuffer?: boolean;
    };
    const scenePass = pass(scene, camera);

    // Only attach MRT when an effect actually needs extra buffers.
    // setMRT({ output }) alone was producing an empty frame on WebGPU.
    if (needsMrt) {
      const mrtSpec: Record<string, unknown> = { output };
      if (needsNormal) {
        // World normals are 0..1; GTAO/SSGI use view normals.
        mrtSpec.normal = needsWorldNormal
          ? vec4(normalWorld.mul(0.5).add(0.5), positionView.z)
          : normalView;
      }
      if (needsDiffuse) mrtSpec.diffuseColor = diffuseColor;
      if (needsVelocity) mrtSpec.velocity = velocity;
      if (needsPackedDepth) {
        // World-space position; alpha=1 marks a written fragment (clear is 0).
        mrtSpec.packedDepth = vec4(positionWorld, 1);
      }
      scenePass.setMRT(mrt(mrtSpec as never));
    }

    const beauty = scenePass;
    const depthTex = scenePass.getTextureNode("depth");
    const packedDepth = needsPackedDepth
      ? scenePass.getTextureNode("packedDepth")
      : undefined;
    const packedDepthTexture = needsPackedDepth
      ? (
          scenePass as unknown as { getTexture: (name: string) => Texture }
        ).getTexture("packedDepth")
      : undefined;
    let depthTexture: Texture | undefined;
    try {
      depthTexture = (
        scenePass as unknown as { getTexture: (name: string) => Texture }
      ).getTexture("depth");
    } catch {
      depthTexture = undefined;
    }
    const normal = needsNormal
      ? scenePass.getTextureNode("normal")
      : undefined;
    const diffuse = needsDiffuse
      ? scenePass.getTextureNode("diffuseColor")
      : undefined;
    const velocityTex = needsVelocity
      ? scenePass.getTextureNode("velocity")
      : undefined;
    const ctx: TslEffectContext = {
      scene,
      camera,
      beauty,
      depth: depthTex,
      packedDepth,
      packedDepthTexture,
      depthTexture,
      normal,
      diffuse,
      velocity: velocityTex,
      reversedDepth: !!renderer.reversedDepthBuffer,
    };

    const byOrder = (a: TslEffect, b: TslEffect) =>
      (a.order ?? 0) - (b.order ?? 0);
    const mainEffects = effects
      .filter((e) => (e.branch ?? "main") === "main")
      .sort(byOrder);
    const overlayEffects = effects
      .filter((e) => e.branch === "overlay")
      .sort(byOrder);
    const postEffects = effects
      .filter((e) => e.branch === "post")
      .sort(byOrder);

    // Beauty is always the base. Main/overlay/post only fold on top when
    // those effects are active; with no main effects this stays beauty alone.
    // Keep PassNode when !needsMrt so post TRAA can take the scene pass as beauty.
    const beautyColor: unknown = needsMrt
      ? scenePass.getTextureNode("output")
      : scenePass;
    let main: unknown = beautyColor;
    for (const effect of mainEffects) {
      main = effect.compose(main, ctx);
    }

    // Overlay: alpha-over onto beauty/main (outline). Additive wash fails on
    // bright / empty backgrounds — edge must replace, not add.
    let composed: unknown = main;
    for (const effect of overlayEffects) {
      const overlay = effect.composeOverlay?.(ctx) ?? null;
      if (overlay == null) continue;
      const o = overlay as { rgb: unknown; a: unknown };
      composed = mix(composed as never, o.rgb as never, o.a as never);
    }

    // Post branch (TRAA, …) — after merge; skipped when TAA off.
    for (const effect of postEffects) {
      composed = effect.compose(composed, ctx);
    }

    // Effects that already applied tone mapping / sRGB set
    // needsOutputColorTransform: false (e.g. former FXAA + renderOutput).
    this._pipeline!.outputColorTransform = !effects.some(
      (e) => e.needsOutputColorTransform === false,
    );
    this._scenePass = scenePass;
    this._pipeline!.outputNode = (composed ?? beautyColor) as never;
    this._pipeline!.needsUpdate = true;
  }
}
